/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import React, { useEffect, useMemo, useState } from 'react';
import { format as formatDate } from 'date-fns';
import {
  Box, Button, Grid, IconButton, MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { DateRangePicker } from 'rsuite';
import { DateRange } from 'rsuite/esm/DateRangePicker/types';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { predefinedRanges } from '../../Common/SearchHeader/utils/DatePickerRange';
import { exportToExcelData } from '../../Common/PageHeader/helper';
import ComponentPageHeader from '../../Common/PageHeader/ComponentPageHeader';
import DataTable from '../../Common/DataTable';
import {
  VesselInformationRes, VesselInformationListingInterface, FilterBlock, FilterCondition,
  FilterPayloadData,
} from './utils/type';
import { VesselInformationTableColumns } from './utils/helper';
import {
  resetIsVesselDeleted, deleteVesselStart, getVesselInformationStart, setShowFilterBuilder, setVesselFilters,
  getFilterMetadata,
} from './redux/sliceVesselInformation';
import { RootState } from '../../redux/rootState';
import { setLoader } from '../../redux/loaderSlice';
import { convertDateFormat, stripCustPrefix } from '../../utils/CommonFunctions';
import {
  resetPagination, setPageNumber, setPageSize, setSearchParam,
  setSortColumn,
  setSortDirection,
} from '../taiReportingSystem/redux/sliceReportWorkspace';
import DeleteVesselConfirmationModal from '../../Common/DeleteVesselConfirmationModal/ComponentDeleteModal';
import { showErrorToaster } from '../../Common/ComponentToast/ComponentSuccessToasts';

function VesselInformation() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tabId } = useParams();

  const {
    vesselInformation, totalVesselInfo, isLoading, isVesselDeleted, showFilterBuilder, vesselFilters, filterMetadata, isFilterDataLoading,
  } = useSelector((state:RootState) => state.VesselInformationReducer);

  const makeCondition = (id: number, join: 'AND' | 'OR' = 'AND'): FilterCondition => ({
    id, join, field: filterMetadata?.[0]?.Field || 'customerid', operator: filterMetadata?.[0]?.Operators?.[0] || 'equals', value: '',
  });
  const formatFieldLabel = (field: string) => {
    switch (field) {
      case 'joborder':
        return 'Job Order #';
      case 'customerid':
        return 'Customer ID';
      case 'customername':
        return 'Customer Name';
      case 'siteid':
        return 'Site ID';
      case 'sitename':
        return 'Site Name';
      case 'tubecount':
        return 'Tube Count';
      case 'vesselname':
        return 'Vessel Name';
      case 'model':
        return 'Model';
      case 'serialnumber':
        return 'Serial #';
      case 'materialtype':
        return 'Material';
      case 'testend':
        return 'Test End';
      case 'offset':
        return 'Offset';
      case 'tubetype':
        return 'Tube Type';
      case 'analyst':
        return 'Analyst';
      case 'inspectiondate':
        return 'Inspection Date';
      case 'manufacturer':
        return 'Manufacturer';
      case 'style':
        return 'Style';
      case 'type':
        return 'Type';
      case 'retestdate':
        return 'Re-Inspection Date';
      default:
        return field;
    }
  };

  const [showConfirmationModal, setShowConfirmationModal] = useState('');
  const [filterBlocks, setFilterBlocks] = useState<FilterBlock[]>(vesselFilters.length ? vesselFilters : [{ id: 1, conditions: [makeCondition(1)] }]);

  // const [page, setPage] = useState(1);
  // const [rowsPerPage, setRowsPerPage] = useState(10);
  // const [searchQuery, setSearchQuery] = useState('');
  const { historyStack } = useSelector((state:RootState) => state.masterDataReducer);
  const {
    pageNumber, pageSize, searchParam, sortColumn, sortDirection,
  } = useSelector((state: RootState) => state.reportWorkspaceReducer);

  const showFilters = () => dispatch(setShowFilterBuilder(!showFilterBuilder));
  const updateCondition = (blockId: number, id: number, update: Partial<FilterCondition>) => setFilterBlocks((blocks) => blocks.map((block) => (block.id === blockId ? { ...block, conditions: block.conditions.map((item) => (item.id === id ? { ...item, ...update } : item)) } : block)));
  const addCondition = (blockId: number, join: 'AND' | 'OR', afterConditionId?: number) => setFilterBlocks((blocks) => blocks.map((block) => {
    if (block.id !== blockId) return block;
    const condition = makeCondition(Date.now(), join);
    if (join === 'OR' && afterConditionId !== undefined) {
      const index = block.conditions.findIndex((item) => item.id === afterConditionId);
      if (index >= 0) {
        const conditions = [...block.conditions];
        conditions.splice(index + 1, 0, condition);
        return { ...block, conditions };
      }
    }
    return { ...block, conditions: [...block.conditions, condition] };
  }));
  const addFilterBlock = () => setFilterBlocks((blocks) => [...blocks, { id: Date.now(), conditions: [makeCondition(Date.now())] }]);
  const removeFilterBlock = (blockId: number) => setFilterBlocks((blocks) => (blocks.length > 1 ? blocks.filter((block) => block.id !== blockId) : blocks));
  const removeCondition = (blockId: number, id: number) => setFilterBlocks((blocks) => blocks.map((block) => (block.id === blockId && block.conditions.length > 1 ? { ...block, conditions: block.conditions.filter((item) => item.id !== id) } : block)));
  const runFilters = () => {
    if (filterBlocks.some((block) => block.conditions.some((condition) => String(condition?.value ?? '').trim() === ''))) {
      showErrorToaster('Some filter conditions are missing values. Please enter the required values or remove any unnecessary conditions to proceed.');
      return;
    }
    dispatch(setVesselFilters(filterBlocks));
    dispatch(setPageNumber(1));
  };
  const clearFilters = () => {
    setFilterBlocks([{ id: Date.now(), conditions: [makeCondition(Date.now())] }]);
    if (vesselFilters.length > 0) {
      dispatch(setVesselFilters([]));
      dispatch(setPageNumber(1));
    }
  };

  const handleData = () => {
    let advancedFilters: FilterPayloadData[] = [];
    let outerFilters: { filters: FilterPayloadData[] }[] = [];
    if (vesselFilters.length > 0) {
      advancedFilters = vesselFilters?.[0]?.conditions?.map((condition, index) => ({
        field: condition.field,
        operator: condition.operator,
        value: condition.value,
        condition: index === 0 ? null : condition.join,
      })) || [];
      outerFilters = vesselFilters?.slice(1)?.map((block) => ({
        filters: block?.conditions?.map((condition, i) => ({
          field: condition?.field, operator: condition?.operator, value: condition?.value, condition: i === 0 ? null : condition?.join,
        })),
      }));
    }
    const payload = {
      pageNumber,
      pageSize,
      searchItem: decodeURIComponent(searchParam),
      sortColumn,
      sortDirection,
      advancedFilters,
      outerFilters,
    };
    dispatch(getVesselInformationStart(payload));
  };

  useEffect(() => {
    if (isLoading || isFilterDataLoading) {
      dispatch(setLoader(true));
    } else {
      dispatch(setLoader(false));
    }
  }, [isLoading, isFilterDataLoading]);

  const tableData: VesselInformationListingInterface[] = useMemo(() => {
    if (vesselInformation?.length > 0) {
      const transformedData = vesselInformation.map((ele, index) => ({
        sn: index,
        action: 'edit&delete',
        jobOrder_infoTool: ele.JobOrder,
        vesselName: ele.VesselName,
        materialType: ele.MaterialType,
        testEnd: ele.TestEnd,
        tubeType: ele.TubeType,
        analyst: ele.Analyst,
        inspectionDate: convertDateFormat(ele.InspectionDate),
        infoText: ele.ParentJob || '',
        isJobCopied: ele.IsJobCopied,
        id: `${ele.JobOrder}-${ele.VesselId}`,
        CustomerId: stripCustPrefix(ele.CustomerId),
        CustomerName: ele.CustomerName,
        SiteId: ele.SiteId,
        SiteName: ele.SiteName,
        Model: ele.Model,
        SerialNumber: ele.SerialNumber,
        offset: ele.offset,
        TubeCount: ele.TubeCount,
        Manufacturer: ele.Manufacturer,
        Style: ele.Style,
        Type: ele.Type,
        RetestDate: convertDateFormat(ele.RetestDate),
      }));
      return transformedData;
    }
    return [];
  }, [vesselInformation]);

  const handleEdit = (id: string) => {
    const record = vesselInformation.find((ele:VesselInformationRes) => `${ele.JobOrder}-${ele.VesselId}` === id);
    if (record) {
      navigate(tabId ? `${record.JobOrder}/${record.VesselId}/${tabId}` : `${record.JobOrder}/${record.VesselId}`);
    }
  };
  const handleDelete = (id: string) => {
    const record = vesselInformation.find((ele:VesselInformationRes) => `${ele.JobOrder}-${ele.VesselId}` === id);
    if (record) {
      const pay = {
        JobOrder: record.JobOrder,
        VesselId: String(record.VesselId),
      };
      dispatch(deleteVesselStart(pay));
    }
  };

  useEffect(() => {
    if (filterMetadata.length === 0) {
      dispatch(getFilterMetadata());
    }
  }, []);
  useEffect(() => {
    handleData();
  }, [pageNumber, pageSize, searchParam, sortColumn, sortDirection, vesselFilters]);
  useEffect(() => {
    if (isVesselDeleted) {
      handleData();
      dispatch(resetIsVesselDeleted());
    }
  }, [isVesselDeleted]);

  const excelData = useMemo(() => vesselInformation?.map((item) => {
    const transformedDataItems = {
      JobOrder: item.JobOrder,
      CustomerId: stripCustPrefix(item.CustomerId),
      CustomerName: item.CustomerName,
      SiteId: item.SiteId,
      SiteName: item.SiteName,
      VesselName: item.VesselName,
      Model: item.Model,
      SerialNumber: item.SerialNumber,
      MaterialType: item.MaterialType,
      TestEnd: item.TestEnd,
      Offset: item.offset,
      TubeType: item.TubeType,
      TubeCount: item.TubeCount,
      Manufacturer: item.Manufacturer,
      Style: item.Style,
      Type: item.Type,
      Analyst: item.Analyst,
      InspectionDate: convertDateFormat(item.InspectionDate),
      RetestDate: convertDateFormat(item.RetestDate),
    };
    return transformedDataItems;
  }), [vesselInformation]);

  const downloadExcel = () => {
    exportToExcelData(
      {
        data: excelData,
      },
      'VESSEL INFORMATION',
    );
  };

  useEffect(() => {
    if (historyStack) {
      const finderString = 'vessel-information/';
      const last = historyStack[historyStack.length - 1];
      const sLast = historyStack[historyStack.length - 2];
      if (!(last?.includes(finderString) || (sLast?.includes(finderString) && last?.includes('vessel-information')))) {
        dispatch(resetPagination());
        clearFilters();
        dispatch(setShowFilterBuilder(false));
      }
    }
  }, [historyStack]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} md={12}>
        <ComponentPageHeader subHeading="VESSEL INFORMATION" downloadExcel={downloadExcel} />
      </Grid>
      <Grid
        item
        xs={12}
        sm={12}
        md={12}
        className="dataTableCustomMargin"
        sx={{
          maxHeight: 'calc(100vh - 120px)', overflowY: 'auto', overflowX: 'hidden', pb: 2,
        }}
      >
        {showFilterBuilder && (
        <Box sx={{
          mb: 2, p: 2, border: '1px solid #00a751', borderRadius: 1, backgroundColor: '#f2fbf6',
        }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1}>
              <Button size="small" sx={{ color: '#00a751', borderColor: '#00a751', '&:hover': { color: '#008f45', borderColor: '#008f45', backgroundColor: '#e7f6ed' } }} variant="outlined" onClick={addFilterBlock}>OR (new group)</Button>
              <Button size="small" sx={{ backgroundColor: '#00a751', '&:hover': { backgroundColor: '#008f45' } }} variant="contained" onClick={runFilters}>Run</Button>
              <Button size="small" sx={{ color: '#00a751' }} onClick={clearFilters}>Clear</Button>
            </Stack>
            <IconButton aria-label="Hide filters" title="Hide filters" size="small" onClick={() => dispatch(setShowFilterBuilder(false))}>
              <FilterListOffIcon />
            </IconButton>
          </Stack>
          <Typography sx={{ mb: 1, fontWeight: 600 }}>All of these conditions must be met</Typography>
          {filterBlocks?.[0]?.conditions?.map((condition, index) => {
            const metadata = filterMetadata?.find((item) => item?.Field === condition?.field) || {
              Field: '', Type: 'Text', Operators: [], Values: [],
            };
            return (
              <Stack key={condition.id} direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 1, alignItems: { md: 'center' } }}>
                {index > 0 && condition.join === 'OR' && <Typography sx={{ minWidth: 35, color: '#00a751' }}>or</Typography>}
                <Select size="small" value={condition.field} onChange={(event) => { const next = filterMetadata?.find((item) => item.Field === event.target.value) || filterMetadata[0]; updateCondition(filterBlocks[0].id, condition.id, { field: event.target.value, operator: next.Operators[0], value: '' }); }} sx={{ minWidth: 170, backgroundColor: '#fff' }}>
                  {filterMetadata?.map((item) => <MenuItem key={item.Field} value={item.Field}>{formatFieldLabel(item.Field)}</MenuItem>)}
                </Select>
                <Select size="small" value={condition.operator || ''} onChange={(event) => updateCondition(filterBlocks[0].id, condition.id, { operator: event.target.value })} sx={{ minWidth: 170, backgroundColor: '#fff' }}>
                  {metadata.Operators.map((operator) => <MenuItem key={operator} value={operator}>{operator}</MenuItem>)}
                </Select>
                {metadata.Type === 'Date' ? <DateRangePicker value={condition.value ? condition.value.split('|').map((date) => new Date(date)) as DateRange : null} onChange={(range: DateRange | null) => updateCondition(filterBlocks[0].id, condition.id, { value: range ? `${formatDate(range[0], 'MM-dd-yyyy')}|${formatDate(range[1], 'MM-dd-yyyy')}` : '' })} ranges={predefinedRanges} placeholder="Select Date Range" character=" - " showCleanButton format="MM-dd-yyyy" placement="bottomStart" /> : metadata.Values.length ? (
                  <Select size="small" displayEmpty value={condition.value} onChange={(event) => updateCondition(filterBlocks[0].id, condition.id, { value: event.target.value })} sx={{ minWidth: 190, backgroundColor: '#fff' }}>
                    <MenuItem value=""><em>Select value</em></MenuItem>
                    {metadata.Values.map((value) => <MenuItem key={String(value)} value={String(value)}>{String(value)}</MenuItem>)}
                  </Select>
                ) : <TextField size="small" value={condition.value} placeholder="Value" inputProps={{ maxLength: metadata.Type === 'Text' ? 256 : undefined, inputMode: metadata.Type === 'Number' ? 'numeric' : undefined }} onChange={(event) => updateCondition(filterBlocks[0].id, condition.id, { value: metadata.Type === 'Number' ? event.target.value.replace(/\D/g, '') : event.target.value })} sx={{ minWidth: 190, backgroundColor: '#fff' }} />}
                <IconButton aria-label="Remove condition" disabled={filterBlocks[0].conditions.length === 1} onClick={() => removeCondition(filterBlocks[0].id, condition.id)} size="small" sx={{ color: '#f06548' }}><DeleteOutlineIcon /></IconButton>
                <Button size="small" sx={{ color: '#00a751', borderColor: '#00a751', '&:hover': { color: '#008f45', borderColor: '#008f45', backgroundColor: '#e7f6ed' } }} variant="outlined" onClick={() => addCondition(filterBlocks[0].id, 'AND')}>AND</Button>
                <Button size="small" sx={{ color: '#00a751', borderColor: '#00a751', '&:hover': { color: '#008f45', borderColor: '#008f45', backgroundColor: '#e7f6ed' } }} variant="outlined" onClick={() => addCondition(filterBlocks[0].id, 'OR', condition.id)}>OR</Button>
              </Stack>
            );
          })}
          {filterBlocks.slice(1).map((block) => (
            <Box key={block.id} sx={{ mt: 2, pt: 1, borderTop: '1px solid #00a751' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ mb: 1, color: '#000', fontWeight: 600 }}>OR all of these conditions must be met</Typography>
                <IconButton aria-label="Remove filter group" onClick={() => removeFilterBlock(block.id)} size="small" sx={{ color: '#f06548' }}><DeleteOutlineIcon /></IconButton>
              </Stack>
              {block.conditions.map((condition, index) => {
                const metadata = filterMetadata.find((item) => item.Field === condition.field) || {
                  Field: '', Type: 'Text', Operators: [], Values: [],
                }; return (
                  <Stack key={condition.id} direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 1 }}>
                    {index > 0 && condition.join === 'OR' && <Typography sx={{ minWidth: 35, color: '#00a751' }}>or</Typography>}
                    <Select size="small" value={condition.field} onChange={(event) => { const next = filterMetadata.find((item) => item.Field === event.target.value) || filterMetadata[0]; updateCondition(block.id, condition.id, { field: event.target.value, operator: next.Operators[0], value: '' }); }} sx={{ minWidth: 170, backgroundColor: '#fff' }}>{filterMetadata.map((item) => <MenuItem key={item.Field} value={item.Field}>{formatFieldLabel(item.Field)}</MenuItem>)}</Select>
                    <Select size="small" value={condition.operator} onChange={(event) => updateCondition(block.id, condition.id, { operator: event.target.value })} sx={{ minWidth: 170, backgroundColor: '#fff' }}>{metadata.Operators.map((operator) => <MenuItem key={operator} value={operator}>{operator}</MenuItem>)}</Select>
                    {metadata.Type === 'Date' ? <DateRangePicker value={condition.value ? condition.value.split('|').map((date) => new Date(date)) as DateRange : null} onChange={(range: DateRange | null) => updateCondition(block.id, condition.id, { value: range ? `${formatDate(range[0], 'MM-dd-yyyy')}|${formatDate(range[1], 'MM-dd-yyyy')}` : '' })} ranges={predefinedRanges} placeholder="Select Date Range" character=" - " showCleanButton format="MM-dd-yyyy" placement="bottomStart" /> : metadata.Values.length ? (
                      <Select size="small" displayEmpty value={condition.value} onChange={(event) => updateCondition(block.id, condition.id, { value: event.target.value })} sx={{ minWidth: 190, backgroundColor: '#fff' }}>
                        <MenuItem value=""><em>Select value</em></MenuItem>
                        {metadata.Values.map((value) => <MenuItem key={String(value)} value={String(value)}>{String(value)}</MenuItem>)}
                      </Select>
                    ) : <TextField size="small" value={condition.value} placeholder="Value" inputProps={{ maxLength: metadata.Type === 'Text' ? 256 : undefined, inputMode: metadata.Type === 'Number' ? 'numeric' : undefined }} onChange={(event) => updateCondition(block.id, condition.id, { value: metadata.Type === 'Number' ? event.target.value.replace(/\D/g, '') : event.target.value })} sx={{ minWidth: 190, backgroundColor: '#fff' }} />}
                    <IconButton aria-label="Remove condition" disabled={block.conditions.length === 1} onClick={() => removeCondition(block.id, condition.id)} size="small" sx={{ color: '#f06548' }}><DeleteOutlineIcon /></IconButton>
                    <Button size="small" sx={{ color: '#00a751', borderColor: '#00a751', '&:hover': { color: '#008f45', borderColor: '#008f45', backgroundColor: '#e7f6ed' } }} variant="outlined" onClick={() => addCondition(block.id, 'AND')}>AND</Button>
                    <Button size="small" sx={{ color: '#00a751', borderColor: '#00a751', '&:hover': { color: '#008f45', borderColor: '#008f45', backgroundColor: '#e7f6ed' } }} variant="outlined" onClick={() => addCondition(block.id, 'OR', condition.id)}>OR</Button>
                  </Stack>
                );
              })}
            </Box>
          ))}
        </Box>
        )}
        <DataTable<VesselInformationListingInterface>
          data={tableData?.length > 0 ? tableData : []}
          columns={VesselInformationTableColumns}
          totalRecords={totalVesselInfo}
          page={pageNumber}
          setPage={(e) => { dispatch(setPageNumber(e)); }}
          rowsPerPage={pageSize}
          setRowsPerPage={(e) => { dispatch(setPageSize(e)); }}
          searchQuery={searchParam}
          setSearchQuery={(e) => { dispatch(setSearchParam(e)); dispatch(setPageNumber(1)); }}
          sortCol={sortColumn}
          setSortCol={(e) => { dispatch(setSortColumn(e)); dispatch(setPageNumber(1)); }}
          sortDir={sortDirection}
          setSortDir={(e) => { dispatch(setSortDirection(e)); dispatch(setPageNumber(1)); }}
          handleData={handleData}
          handleEdit={handleEdit}
          handleDelete={(id) => setShowConfirmationModal(id)}
          handleDataFromComponent
          buttonText="Filter"
          handleButtonClick={() => { showFilters(); }}
        />
      </Grid>
      <DeleteVesselConfirmationModal
        show={showConfirmationModal}
        setOpenFrom={() => setShowConfirmationModal('')}
        handleDelete={handleDelete}
      />
    </Grid>
  );
}

export default VesselInformation;
